import { GameVars } from "./../../GameVars";
import { GameConstants } from "../../GameConstants";
import { BoardScene } from "./BoardScene";
import * as Carrom from "carrom-king-engine";
import { BootScene } from "../BootScene";

export class DiskActor {

    public id: number;
    public shadowImg: Phaser.GameObjects.Image;
    public diskImg: Phaser.GameObjects.Image;
    public debugGraphicsContainer: Phaser.GameObjects.Container;
    public isStriker: boolean;
    public color: string;
    public canBePositionedWithCursor: boolean;

    private pos: {x: number, y: number};
    private scene: Phaser.Scene;
    
    public get position(): {x: number, y: number} {

        return this.pos; 
    }

    public set position(p: {x: number, y: number}) {

        this.pos.x = p.x * GameConstants.PHYS_SCALE;
        this.pos.y = p.y * GameConstants.PHYS_SCALE;
 
        if (GameVars.reversedBoard) {
            this.shadowImg.setPosition(- this.pos.x, - this.pos.y);
            this.diskImg.setPosition(- this.pos.x, - this.pos.y);

            if (this.debugGraphicsContainer) {
                this.debugGraphicsContainer.setPosition(- this.pos.x, - this.pos.y);
            }
        } else {
            this.shadowImg.setPosition(this.pos.x, this.pos.y);
            this.diskImg.setPosition(this.pos.x, this.pos.y);

            if (this.debugGraphicsContainer) {
                this.debugGraphicsContainer.setPosition(this.pos.x, this.pos.y);
            }
        }
    }
    
    constructor(scene: Phaser.Scene, isStriker: boolean, color: string, id: number) {

        this.scene = scene;
        this.isStriker = isStriker;
        this.color = color;
        this.id = id;
        this.pos = {x: 0, y: 0};

        const puckRadius = GameVars.carromEngine.puckRadius;
        const strikerRadius = GameVars.carromEngine.strikerRadius;
        
        this.shadowImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "shadow");
        this.shadowImg.setScale(this.isStriker ? 1.625 : 1.625 * puckRadius / strikerRadius);
        this.shadowImg.setOrigin(.5, .43);

        let diskImgName: string;

        if (isStriker) {
            diskImgName = "striker_gameplay_" + GameVars.gameData.strikerId;
        } else {
            
            if (this.color === Carrom.Color.RED) {
                diskImgName = "puck-red";
            } else if (this.color === Carrom.Color.WHITE) {
                diskImgName = "puck-white";
            } else {
                diskImgName = "puck-black";
            }
        }

        this.diskImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", diskImgName);
       
        if (GameConstants.SHOW_DEBUG_GRAPHICS) {

            this.debugGraphicsContainer = new Phaser.GameObjects.Container(scene);

            let r: number;
            let color: number;
            const strikerRadius = GameVars.carromEngine.strikerRadius;

            if (this.isStriker) {
                r = strikerRadius;
                color = 0x0000FF;
            } else {
                r = puckRadius;
                color = 0x000000;
                if (this.color === Carrom.Color.BLACK) {
                    color = 0xFFFFFF;
                } else {
                    color = 0x000000;
                }
            }

            const debugGraphics = new Phaser.GameObjects.Graphics(this.scene);
            debugGraphics.lineStyle(2, color);
            debugGraphics.strokeCircle(0, 0, r * GameConstants.PHYS_SCALE);
            debugGraphics.stroke();

            this.debugGraphicsContainer.add(debugGraphics);

            // EL ID
            if (!isStriker) {
                const idLabel = new Phaser.GameObjects.Text(this.scene, 0, 0, this.id.toString(), { fontFamily: "Arial", fontSize: "22px", color: this.color === Carrom.Color.BLACK ? "#FFFFFF" : "#000000"});
                idLabel.setOrigin(.5,BootScene.originY);
                this.debugGraphicsContainer.add(idLabel);
            }
        } else {
            this.debugGraphicsContainer = null;
        }
    }

    public destroy(): void {

        this.diskImg.destroy();
        this.shadowImg.destroy(); 
        
        if (this.debugGraphicsContainer) {
            this.debugGraphicsContainer.destroy();
        }   
    }

    public hide(): void {

        this.scene.add.tween({
            targets: [this.diskImg, this.shadowImg],
            alpha: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 250,
            delay: 250
        });
    }

    public resetStriker(p: Carrom.Vector2D, strikerId: number, firstShot?: boolean): void {

        const disksContainer = this.diskImg.parentContainer;
        disksContainer.bringToTop(this.diskImg);

        const shadowsContainer = this.shadowImg.parentContainer;
        shadowsContainer.bringToTop(this.shadowImg);

        if (firstShot) {

            this.canBePositionedWithCursor = true;

            this.position = p;

            this.diskImg.setFrame("striker_gameplay_" + strikerId);

            this.diskImg.alpha = 1;
            this.shadowImg.alpha = 1;

        } else {

            this.scene.add.tween({
                targets: [this.diskImg, this.shadowImg],
                alpha: 0,
                ease: Phaser.Math.Easing.Cubic.Out,
                duration: 250,
                delay: 350,
                onComplete: function(): void {

                    this.position = p;
                    this.diskImg.setFrame("striker_gameplay_" + strikerId);
    
                    this.scene.add.tween({
                        targets: [this.diskImg, this.shadowImg],
                        alpha: 1,
                        ease: Phaser.Math.Easing.Cubic.Out,
                        duration: 250,
                        delay: 350,
                        onComplete: function(): void {
                            this.canBePositionedWithCursor = true;
                        },
                        onCompleteScope: this
                    });
                },
                onCompleteScope: this
            });
        }
    }

    public tint(): void {

        this.diskImg.tint = 0xFFAAAA;
    }

    public unTint(): void {

        this.diskImg.tint = 0xFFFFFF;
    }

    public pocketed(): void {

        let lengthPucks = 0;
        let isRed = false;

        for (let i = 0; i < GameVars.pucks.length; i++) {
            if (GameVars.pucks[i].color === Carrom.Color.RED) {
                isRed = true;
            } else if (GameVars.gameMode === Carrom.GameMode.FREESTYLE) {
                lengthPucks ++;
            } else if (this.color === GameVars.pucks[i].color) {
                lengthPucks ++;
            }
        }

        if (this.color !== Carrom.Color.RED && isRed && lengthPucks === 1) {
            this.scene.tweens.add({
                targets: [this.shadowImg, this.diskImg],
                alpha: 0,
                ease: Phaser.Math.Easing.Cubic.Out,
                duration: 400,
                delay: 700,
                onComplete: function(): void {
                    this.shadowImg.destroy();
                    this.diskImg.destroy();
                    this.destroy();
                    
                },
                onCompleteScope: this
            });
        } else {
            let isPlayer: boolean;

            const disksContainer = this.diskImg.parentContainer;
            disksContainer.bringToTop(this.diskImg);
    
            this.scene.tweens.add({
                targets: this.shadowImg,
                alpha: 0,
                ease: Phaser.Math.Easing.Cubic.Out,
                duration: 400,
                delay: 700,
                onComplete: function(): void {
                    this.shadowImg.destroy();
                },
                onCompleteScope: this
            });
    
            let x: number;
            const y = BoardScene.puckIconPosition.y;
            
            if (GameVars.gameMode === Carrom.GameMode.PROFESSIONAL) {
    
                switch (this.color) {
    
                    case Carrom.Color.RED:
    
                            if (GameVars.currentTurnPlayerId === GameVars.playerData.id) {
                                isPlayer = true;
                                x = BoardScene.puckIconPosition.x;
                            }  else {
                                isPlayer = false;
                                x = -BoardScene.puckIconPosition.x;
                            }
                        
                            break;
                        case Carrom.Color.WHITE:
                            isPlayer = GameVars.reversedBoard;
                            x = GameVars.reversedBoard ? BoardScene.puckIconPosition.x : -BoardScene.puckIconPosition.x;
                            break;
                        case Carrom.Color.BLACK:
                            isPlayer = !GameVars.reversedBoard;
                            x = GameVars.reversedBoard ? -BoardScene.puckIconPosition.x : BoardScene.puckIconPosition.x;
                            break;
                    default:
                        break;
                }
    
            } else {
    
                if (GameVars.currentTurnPlayerId === GameVars.playerData.id) {
                    isPlayer = true;
                    x = BoardScene.puckIconPosition.x;
                }  else {
                    isPlayer = false;
                    x = -BoardScene.puckIconPosition.x;
                }
            }
    
            this.diskImg.tint = 0xCCCCCC;
    
            const t = Math.floor(Math.sqrt((this.diskImg.x - x) * (this.diskImg.x - x) + (this.diskImg.y - y) * (this.diskImg.y - y)) / 1.5);
    
            this.scene.tweens.add({
                targets: this.diskImg,
                x: x,
                y: y,
                ease: Phaser.Math.Easing.Cubic.Out,
                duration: t,
                delay: 700,
                onComplete: function(): void {
                    BoardScene.currentInstance.onPuckActorArrivedtoHUD(this.color, isPlayer);
                    this.destroy();
                },
                onCompleteScope: this
            });
    
            this.scene.tweens.add({
                targets: this.diskImg,
                alpha: 0,
                ease: Phaser.Math.Easing.Cubic.Out,
                duration: 350,
                delay: 900
            });
        }
    }
}
